import React, { Component } from "react";
import RTo3Service from "../../services/rto3service";

// R-to-3 Welcome Reward CRM. Reads Admin_RTo3_Summary / Admin_RTo3_List via the Store WebAPI.
// Styles are scoped under .r3-root so they don't touch the rest of the portal.
const STAGE_TH = { 1: [24, 72, 120], other: [120, 288, 408] }; // reminder hours per stage (stage 2&3 = Day 5/12/17)
const STATUS = { converting: "Converting", pending: "Pending", expiring: "Expiring", lastcall: "Last call", lapsed: "Lapsed", converted: "Converted" };
// Registration-source badge colour class (source strings come from Admin_RTo3_List.RegSource)
const srcCls = (s) => ({ "iOS App": "ios", "Android App": "android", "Website": "web", "Store / Walk-in": "store", "WhatsApp": "wa", "Delivery App": "del" }[s] || "other");
// Conversion type (from Admin_RTo3_List.ConversionType): Reward / Natural / Other:<code> / "".
const convInfo = (ct) => {
  if (!ct) return { label: "—", cls: "none" };
  if (ct === "Reward") return { label: "Reward", cls: "reward" };
  if (ct === "Natural") return { label: "Natural", cls: "natural" };
  if (ct.indexOf("Other:") === 0) return { label: "Other · " + ct.slice(6), cls: "other" };
  return { label: ct, cls: "none" };
};

export default class RTo3 extends Component {
  constructor(props) {
    super(props);
    this.service = new RTo3Service();
    this.state = { summary: null, rows: [], filter: "all", search: "", from: "", to: "", loading: true, loadingRows: true };
    this._t = null;
  }

  componentDidMount() {
    this.loadSummary();
    this.loadRows();
  }

  loadSummary = async () => {
    this.setState({ loading: true });
    const s = await this.service.getSummary(this.state.from || null, this.state.to || null);
    this.setState({ summary: s && s.enrolled !== undefined ? s : (s || null), loading: false });
  };

  loadRows = async () => {
    this.setState({ loadingRows: true });
    const r = await this.service.getList(this.state.filter, this.state.search, this.state.from || null, this.state.to || null, 1, 100);
    this.setState({ rows: Array.isArray(r) ? r : [], loadingRows: false });
  };

  setFilter = (f) => { this.setState({ filter: f }, this.loadRows); };
  // date-range filter (scopes summary + list by enrolment date; procs take p_FromDate/p_ToDate)
  ymd = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().slice(0, 10); };
  setRange = (from, to) => { this.setState({ from, to }, () => { this.loadSummary(); this.loadRows(); }); };
  applyRange = () => { this.loadSummary(); this.loadRows(); };
  presetDays = (n) => { const t = new Date(); this.setRange(this.ymd(new Date(Date.now() - (n - 1) * 864e5)), this.ymd(t)); };
  presetMonth = () => { const t = new Date(); this.setRange(this.ymd(new Date(t.getFullYear(), t.getMonth(), 1)), this.ymd(t)); };
  onSearch = (e) => {
    const v = e.target.value;
    this.setState({ search: v });
    clearTimeout(this._t);
    this._t = setTimeout(this.loadRows, 350);
  };

  inr = (n) => { const v = Math.round(Number(n) || 0); return v >= 1000 ? "₹" + (v / 1000).toFixed(v % 1000 ? 1 : 0) + "k" : "₹" + v; };
  pct = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);
  hrs = (h) => { if (h == null) return "—"; h = Math.round(h); if (h < 0) h = 0; const d = Math.floor(h / 24), r = h % 24; return d > 0 ? d + "d " + r + "h" : r + "h"; };

  reminders(stage, hoursInStage, bucket) {
    if (bucket === "converted" || bucket === "lapsed") return [{ s: "sent" }, { s: "sent" }, { s: "sent" }];
    const th = stage === 1 ? STAGE_TH[1] : STAGE_TH.other;
    const lab = stage === 1 ? ["R1", "R2", "R3"] : ["N1", "N2", "N3"];
    const h = hoursInStage || 0; const out = []; let due = false;
    for (let i = 0; i < 3; i++) { if (h >= th[i]) out.push({ s: "sent", l: lab[i] }); else if (!due) { out.push({ s: "due", l: lab[i] }); due = true; } else out.push({ s: "", l: lab[i] }); }
    return out;
  }

  nextAction(row, rem) {
    if (row.bucket === "converted") return ["Move to ", <b key="a">loyalty</b>];
    if (row.bucket === "lapsed") return ["Re-engage ", <b key="a">win-back</b>];
    if (row.bucket === "converting") return ["Nudge ", <b key="a">{row.activeStage === 2 ? "2nd" : "3rd"} order</b>];
    for (let i = 0; i < rem.length; i++) if (rem[i].s === "due") return [rem[i].l + " ", <b key="a">due</b>];
    return ["All nudges sent · ", <b key="a">call</b>];
  }
  clockCls = (b) => (b === "lastcall" || b === "lapsed" ? "crit" : b === "expiring" ? "warn" : b === "converted" ? "done" : "ok");

  renderKpis() {
    const k = this.state.summary || {};
    const cards = [
      ["brand", "Enrolled", k.enrolled || 0, ""],
      ["info", "Reward redeemed", k.orderedReward || 0, (k.placed1st || 0) + " delivered · " + Math.max(0, (k.orderedReward || 0) - (k.placed1st || 0)) + " in progress"],
      ["gold", "Fully converted", k.fullyConverted || 0, k.enrolled ? this.pct(k.fullyConverted, k.enrolled) + "% · all 3 RFD" : ""],
      ["warn", "Expiring ≤48h", k.expiringSoon || 0, k.lastCall ? k.lastCall + " last-call ≤12h" : "chase priority"],
      ["crit", "Lapsed", k.lapsed || 0, this.inr((k.lapsed || 0) * 350) + " ₹350 lost"],
      ["good", "Reward earned", this.inr(k.rewardEarned || 0), this.inr(k.rewardInPlay || 0) + " in play"],
    ];
    return cards.map((c, i) => (
      <div className={"r3-kpi b-" + c[0]} key={i}>
        <span className="r3-stripe" />
        <div className="r3-lab">{c[1]}</div>
        <div className="r3-v">{c[2]}</div>
        <div className="r3-d">{c[3] || " "}</div>
      </div>
    ));
  }

  renderFunnel() {
    const f = this.state.summary || {};
    const reg = f.registered || 0, s1 = f.stage1 || 0, s2 = f.stage2 || 0, s3 = f.stage3 || 0;
    const r1 = f.stage1Redeemed || 0, r2 = f.stage2Redeemed || 0, r3 = f.stage3Redeemed || 0;
    const row = (cls, lab, sub, n, denom, avg, drop) => {
      const w = reg > 0 ? Math.max(4, Math.round((n / reg) * 100)) : 0;
      return (
        <React.Fragment key={lab}>
          <div className={"r3-fstage " + cls}>
            <div className="r3-flab">{lab}<small>{sub}</small></div>
            <div className="r3-fbar"><span style={{ width: w + "%" }}><b>{n}</b></span></div>
            <div className="r3-fnum"><div className="n">{n}</div><div className="p">{denom}</div></div>
          </div>
          {avg != null && (
            <div className="r3-ftrans"><div className="tmid">{drop != null && <span className="drop">−{drop}</span>}<span className="dline" /><span className="avg">avg {avg == null ? "—" : avg}d →</span></div></div>
          )}
        </React.Fragment>
      );
    };
    const ordered = f.ordered || 0, oReward = f.orderedReward || 0, oOrganic = (f.orderedNatural || 0) + (f.orderedOther || 0);
    const ow = reg > 0 ? Math.max(4, Math.round((ordered / reg) * 100)) : 0;
    return (
      <div className="r3-funnel">
        {row("s0", "Registered", "Enrolled in ₹350", reg, "100%", null, null)}
        <div className="r3-fstage conv">
          <div className="r3-flab">Placed a first order<small>any first order</small></div>
          <div className="r3-fbar"><span style={{ width: ow + "%" }}><b>{ordered}</b></span></div>
          <div className="r3-fnum"><div className="n">{ordered}</div><div className="p">{this.pct(ordered, reg)}% of enrolled</div></div>
        </div>
        <div className="r3-fsplit"><span className="r">{oReward} reward</span><span className="sep">·</span><span className="o">{oOrganic} organic</span></div>
        <div className="r3-fladhead">Reward ladder · redeemed <small>(→ delivered = reached RFD)</small></div>
        {row("s1", "1st reward · ₹150", "WELCOME150", r1, s1 + " delivered · " + this.pct(r1, reg) + "% of enrolled", f.avgDays1To2, null)}
        {row("s2", "2nd reward · ₹100", "WELCOME100A", r2, s2 + " delivered · " + this.pct(r2, r1) + "% of 1st", f.avgDays2To3, null)}
        {row("s3", "3rd reward · ₹100", "WELCOME100B", r3, s3 + " delivered · " + this.pct(r3, r2) + "% of 2nd", null, null)}
      </div>
    );
  }

  renderCadence(title, rows) {
    return (
      <div className="r3-cad">
        <div className="r3-cadsub">{title}</div>
        <div className="r3-vt">
          {rows.map((r, i) => (
            <div className={"r3-vtrow " + (r.c || "")} key={i}>
              <div className="r3-vtnode"><span className="dot" />{i < rows.length - 1 && <span className="line" />}</div>
              <div className="r3-vtlbl"><span className="t">{r.t}</span><span className="s">{r.s}</span></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderConvMix() {
    const f = this.state.summary || {};
    const total = f.enrolled || 0;
    if (!total) return null;
    const reward = f.orderedReward || 0, natural = f.orderedNatural || 0, other = f.orderedOther || 0;
    const none = Math.max(0, total - (f.ordered || 0));
    const seg = (n, color, title) => (n > 0 ? <span key={title} style={{ width: (100 * n / total) + "%", background: color }} title={title + " " + n} /> : null);
    const leg = (n, color, label) => <span className="r3-cmix-leg" key={label}><span className="dot" style={{ background: color }} />{label} <b>{n}</b></span>;
    return (
      <div className="r3-cmix">
        <div className="r3-cmix-lab">Conversion mix · {total} enrolled</div>
        <div className="r3-cmix-bar">{seg(reward, "#a9781f", "Reward")}{seg(natural, "#1f9d5b", "Natural")}{seg(other, "#3367d6", "Other coupon")}{seg(none, "#efeaf2", "No order")}</div>
        <div className="r3-cmix-legs">{leg(reward, "#a9781f", "Reward")}{leg(natural, "#1f9d5b", "Natural")}{leg(other, "#3367d6", "Other coupon")}{leg(none, "#cfc7d6", "No order yet")}</div>
      </div>
    );
  }

  renderRows() {
    const { rows, loadingRows } = this.state;
    if (loadingRows) return (<tr><td colSpan="10" className="r3-empty">Loading…</td></tr>);
    if (!rows.length) return (<tr><td colSpan="10" className="r3-empty">No enrolments yet — this lights up when the Welcome Reward program is live.</td></tr>);
    return rows.map((r, idx) => {
      // Redeemed a stage but the next hasn't unlocked yet (their order isn't at RFD) →
      // no active stage. Show an "awaiting delivery" state instead of blanks / a stale R1-due.
      const awaitingRfd = (r.redeemed || 0) > 0 && (r.activeStage == null) && r.bucket !== "converted" && r.bucket !== "lapsed";
      const rem = awaitingRfd ? [{ s: "sent" }, { s: "sent" }, { s: "sent" }] : this.reminders(r.activeStage || 1, r.hoursInStage, r.bucket);
      const pips = [1, 2, 3].map((i) => { const on = (r.redeemedRFD || 0) >= i, cur = r.activeStage === i; return <span key={i} className={"r3-pip" + (on ? " on" : cur ? " cur" : "")} />; });
      const stageTxt = awaitingRfd ? "Order in progress" : r.bucket === "lapsed" ? "Never converted" : r.bucket === "converted" ? "All 3 orders" : (r.activeStage === 1 ? "Registered" : "Awaiting " + (r.activeStage === 2 ? "2nd" : "3rd"));
      const deadline = awaitingRfd ? <div className="r3-clock ok">At delivery<small>unlocks next</small></div>
        : r.bucket === "lapsed" ? <div className="r3-clock crit">Expired</div>
        : r.bucket === "converted" ? <div className="r3-clock done">Complete</div>
        : <div className={"r3-clock " + this.clockCls(r.bucket)}>{this.hrs(r.hoursToExpiry)}<small>left</small></div>;
      const conv = convInfo(r.conversionType);
      return (
        <tr className={"r3-st-" + r.bucket} key={idx}>
          <td className="r3-lead"><div className="r3-cust"><b>{r.customerName || "#" + r.customerID}</b><small>+91 {r.mobileNo || ""}</small></div></td>
          <td>{String(r.registeredOn || "").replace("T", " ").slice(0, 16)}</td>
          <td><span className={"r3-src " + srcCls(r.regSource)}>{r.regSource || "—"}</span></td>
          <td><div className="r3-pips">{pips}</div><div className="r3-stagetxt">{stageTxt} · <span className="r3-money" style={Number(r.earned) >= 350 ? { color: "var(--r3-gold)" } : {}}>₹{Math.round(r.earned || 0)}<span className="of"> / ₹350</span></span></div></td>
          <td>{this.hrs(r.hoursInStage)}</td>
          <td>{deadline}</td>
          <td><div className="r3-rdots">{rem.map((x, i) => <span key={i} className={"r3-rdot" + (x.s ? " " + x.s : "")}>{x.l || ""}</span>)}</div></td>
          <td><span className={"r3-chip-s s-" + (awaitingRfd ? "converting" : r.bucket)}><span className="dot" />{awaitingRfd ? "In progress" : (STATUS[r.bucket] || r.bucket)}</span></td>
          <td>{conv.cls === "none" ? <span className="r3-conv-none">—</span> : <span className={"r3-conv c-" + conv.cls}>{conv.label}</span>}</td>
          <td><div className="r3-nextact">{awaitingRfd ? ["Awaiting ", <b key="a">delivery</b>] : this.nextAction(r, rem)}</div></td>
        </tr>
      );
    });
  }

  render() {
    const filters = [["all", "All"], ["converting", "Converting"], ["pending", "Pending"], ["expiring", "Expiring ≤48h"], ["lastcall", "Last-call ≤12h"], ["lapsed", "Lapsed"], ["converted", "Fully converted"]];
    return (
      <div className="r3-root">
        <style>{R3_CSS}</style>
        <div className="r3-head">
          <h1>R-to-3 · Welcome Reward journey</h1>
          <p>Every ₹350 enrolment from sign-up to the third order — who converted, who's stalling, and who's about to lose the offer. Auto chase-up on WhatsApp + push (9am–8pm).</p>
        </div>

        <div className="r3-drange">
          <span className="r3-drlab">Enrolled</span>
          <button className={"r3-drchip" + (!this.state.from && !this.state.to ? " on" : "")} onClick={() => this.setRange("", "")}>All time</button>
          <button className="r3-drchip" onClick={() => this.presetDays(30)}>Last 30 days</button>
          <button className="r3-drchip" onClick={this.presetMonth}>This month</button>
          <span className="r3-drcustom">
            <input type="date" value={this.state.from} onChange={(e) => this.setState({ from: e.target.value })} />
            <span className="r3-drto">→</span>
            <input type="date" value={this.state.to} onChange={(e) => this.setState({ to: e.target.value })} />
            <button className="r3-drapply" onClick={this.applyRange}>Apply</button>
          </span>
        </div>

        <div className="r3-kpis">{this.renderKpis()}</div>

        {this.renderConvMix()}

        <div className="r3-grid2">
          <div className="r3-panel">
            <div className="r3-panelh"><h2>Conversion funnel</h2><span>Registered → 1st → 2nd → 3rd (RFD) · avg time to next</span></div>
            <div className="r3-panelb">{this.renderFunnel()}</div>
          </div>
          <div className="r3-panel">
            <div className="r3-panelh"><h2>Chase-up cadence</h2><span>Auto-send · WA + push · 9am–8pm</span></div>
            <div className="r3-panelb">
              {this.renderCadence("Stage 1 · 168h from sign-up", [
                { t: "0h", s: "Registered", c: "" },
                { t: "24h", s: "Reminder 1", c: "r" },
                { t: "72h", s: "Reminder 2", c: "r" },
                { t: "120h", s: "Reminder 3 · final", c: "r" },
                { t: "168h", s: "Offer lost — ₹350 gone", c: "exp" },
              ])}
              {this.renderCadence("Stage 2 & 3 · 20-day window from previous RFD", [
                { t: "RFD", s: "Window opens", c: "" },
                { t: "Day 5", s: "Nudge 1", c: "r" },
                { t: "Day 12", s: "Nudge 2", c: "r" },
                { t: "Day 17", s: "Nudge 3 · final", c: "r" },
                { t: "Day 20", s: "Offer lost — ₹100 gone", c: "exp" },
              ])}
              <p className="r3-cadleg">Fires only while the stage's order hasn't landed; stops the instant it does. Overnight sends wait for the 9am slot.</p>
            </div>
          </div>
        </div>

        <div className="r3-filters">
          {filters.map((f) => <span key={f[0]} className={"r3-chip" + (this.state.filter === f[0] ? " on" : "")} onClick={() => this.setFilter(f[0])}>{f[1]}</span>)}
          <span className="r3-search"><input placeholder="Search name or mobile" value={this.state.search} onChange={this.onSearch} /></span>
        </div>

        <div className="r3-tablewrap"><div className="r3-scroll"><table>
          <thead><tr><th>Customer</th><th>Registered</th><th>Source</th><th>Stage · ₹350</th><th>In stage</th><th>Deadline</th><th>Reminders</th><th>Status</th><th>Conversion</th><th>Next action</th></tr></thead>
          <tbody>{this.renderRows()}</tbody>
        </table></div></div>

        <div className="r3-foot"><b>Converted</b> = order reaches RFD. Source: read-only Welcome-Reward procs. Reminders shown are schedule-derived until the auto-sender ships.</div>
      </div>
    );
  }
}

const R3_CSS = `
.r3-root{--r3-card:#fff;--r3-ink:#231a2b;--r3-ink2:#4f4557;--r3-muted:#7a6f83;--r3-faint:#a89fb0;--r3-line:#e8e2ee;--r3-line2:#f0ecf4;--r3-brand:#c71c78;--r3-brandsoft:#f7e8f1;--r3-gold:#a9781f;--r3-goldsoft:#f6edda;--r3-good:#1f9d5b;--r3-goodsoft:#e3f5ec;--r3-warn:#c07d00;--r3-warnsoft:#fbf0d9;--r3-crit:#c0392b;--r3-critsoft:#fbe7e4;--r3-info:#5a52c9;font-variant-numeric:tabular-nums;color:var(--r3-ink);padding:4px 2px 40px}
.r3-root h1,.r3-root h2{margin:0;font-weight:700}
.r3-head h1{font-size:22px}.r3-head p{color:var(--r3-muted);font-size:13px;margin:4px 0 0;max-width:70ch}
.r3-kpis{display:grid;grid-template-columns:repeat(6,1fr);gap:12px;margin-top:18px}
.r3-kpi{background:var(--r3-card);border:1px solid var(--r3-line);border-radius:13px;padding:14px 15px;position:relative;overflow:hidden;box-shadow:0 12px 30px -20px rgba(35,26,43,.25)}
.r3-stripe{position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--r3-faint)}
.b-brand .r3-stripe{background:var(--r3-brand)}.b-good .r3-stripe{background:var(--r3-good)}.b-gold .r3-stripe{background:var(--r3-gold)}.b-warn .r3-stripe{background:var(--r3-warn)}.b-crit .r3-stripe{background:var(--r3-crit)}.b-info .r3-stripe{background:var(--r3-info)}
.r3-lab{font-size:10.5px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--r3-muted)}
.r3-kpi .r3-v{font-size:26px;font-weight:750;margin-top:7px;line-height:1}.r3-kpi .r3-d{font-size:11.5px;color:var(--r3-muted);margin-top:6px}
.r3-grid2{display:grid;grid-template-columns:1.5fr 1fr;gap:16px;margin-top:16px}
.r3-panel{background:var(--r3-card);border:1px solid var(--r3-line);border-radius:14px;box-shadow:0 12px 30px -20px rgba(35,26,43,.22)}
.r3-panelh{display:flex;align-items:center;justify-content:space-between;padding:15px 18px 0}.r3-panelh h2{font-size:15px}.r3-panelh span{font-size:11.5px;color:var(--r3-muted)}
.r3-panelb{padding:16px 18px 18px}
.r3-funnel{display:flex;flex-direction:column;gap:2px}
.r3-fstage{display:grid;grid-template-columns:150px 1fr 120px;align-items:center;gap:14px;padding:9px 0}
.r3-flab{font-size:12.5px;font-weight:700}.r3-flab small{display:block;font-weight:600;color:var(--r3-muted);font-size:10.5px;margin-top:1px}
.r3-fbar{height:26px;border-radius:7px;background:var(--r3-line2);position:relative;overflow:hidden}
.r3-fbar>span{position:absolute;left:0;top:0;bottom:0;border-radius:7px;background:var(--r3-brand)}
.s1 .r3-fbar>span{background:var(--r3-info)}.s2 .r3-fbar>span{background:var(--r3-good)}.s3 .r3-fbar>span{background:var(--r3-gold)}
.r3-fstage.conv .r3-fbar>span{background:#5a52c9}
.r3-fsplit{margin:1px 0 4px 164px;font-size:12px;font-weight:700}.r3-fsplit .r{color:#a9781f}.r3-fsplit .o{color:#1f8a4c}.r3-fsplit .sep{color:var(--r3-faint);margin:0 8px}
.r3-fladhead{font-size:10.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:var(--r3-muted);margin:10px 0 2px;padding-top:9px;border-top:1px solid var(--r3-line2)}
.r3-fbar b{position:absolute;right:9px;top:50%;transform:translateY(-50%);font-size:12px;font-weight:800;color:#fff}
.r3-fnum{text-align:right}.r3-fnum .n{font-size:16px;font-weight:750}.r3-fnum .p{font-size:11px;color:var(--r3-muted)}
.r3-ftrans{display:grid;grid-template-columns:150px 1fr 120px;gap:14px;padding:1px 0}.r3-ftrans .tmid{grid-column:2;display:flex;align-items:center;gap:8px;color:var(--r3-muted);font-size:11px}.r3-ftrans .dline{flex:1;height:1px;background:repeating-linear-gradient(90deg,var(--r3-line) 0 6px,transparent 6px 12px)}.r3-ftrans .drop{color:var(--r3-crit);font-weight:700}.r3-ftrans .avg{color:var(--r3-ink2);font-weight:700}
.r3-cadsub{font-size:10px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:var(--r3-muted);margin:18px 0 12px;padding-bottom:8px;border-bottom:1px solid var(--r3-line2)}
.r3-cad:first-child .r3-cadsub{margin-top:2px}
.r3-vtrow{display:flex;gap:12px;align-items:flex-start}
.r3-vtnode{position:relative;width:12px;flex:none;display:flex;justify-content:center}
.r3-vtnode .dot{width:12px;height:12px;border-radius:50%;background:var(--r3-card);border:2px solid var(--r3-faint);margin-top:1px;position:relative;z-index:1}
.r3-vtrow.r .dot{border-color:var(--r3-brand);background:var(--r3-brand)}
.r3-vtrow.exp .dot{border-color:var(--r3-crit);background:var(--r3-crit)}
.r3-vtnode .line{position:absolute;top:11px;bottom:-7px;left:50%;transform:translateX(-50%);width:2px;background:var(--r3-line2)}
.r3-vtlbl{display:flex;align-items:baseline;gap:9px;padding-bottom:15px;line-height:1.2}
.r3-vtlbl .t{font-size:12px;font-weight:800;color:var(--r3-ink);min-width:44px}
.r3-vtlbl .s{font-size:11.5px;color:var(--r3-muted)}
.r3-vtrow.exp .t,.r3-vtrow.exp .s{color:var(--r3-crit)}
.r3-cadleg{font-size:11.5px;color:var(--r3-muted);margin-top:10px;line-height:1.55}.r3-cadleg b{color:var(--r3-ink2)}
.r3-filters{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:20px 0 12px}
.r3-chip{height:31px;padding:0 12px;border:1px solid var(--r3-line);background:var(--r3-card);border-radius:8px;font-size:12.5px;font-weight:600;color:var(--r3-ink2);cursor:pointer;display:inline-flex;align-items:center}
.r3-chip:hover{border-color:var(--r3-faint)}.r3-chip.on{background:var(--r3-ink);border-color:var(--r3-ink);color:#fff}
.r3-search{margin-left:auto;height:33px;border:1px solid var(--r3-line);background:var(--r3-card);border-radius:9px;display:flex;align-items:center;padding:0 12px;min-width:200px}.r3-search input{border:0;background:transparent;outline:none;color:var(--r3-ink);font-size:13px;width:100%}
.r3-tablewrap{background:var(--r3-card);border:1px solid var(--r3-line);border-radius:14px;box-shadow:0 12px 30px -20px rgba(35,26,43,.22);overflow:hidden}.r3-scroll{overflow-x:auto}
.r3-root table{width:100%;border-collapse:collapse;min-width:940px}
.r3-root thead th{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--r3-muted);text-align:left;padding:12px 14px;border-bottom:1px solid var(--r3-line);background:#faf8fc;white-space:nowrap}
.r3-root tbody td{padding:12px 14px;border-bottom:1px solid var(--r3-line2);vertical-align:middle;font-size:13px}
.r3-root tbody tr:hover{background:#faf8fc}
.r3-cust b{font-weight:700}.r3-cust small{color:var(--r3-muted);font-size:11.5px;display:block}
.r3-lead{border-left:3px solid transparent}
.r3-st-converting .r3-lead{border-left-color:var(--r3-good)}.r3-st-pending .r3-lead{border-left-color:var(--r3-faint)}.r3-st-expiring .r3-lead{border-left-color:var(--r3-warn)}.r3-st-lastcall .r3-lead{border-left-color:var(--r3-crit)}.r3-st-lapsed .r3-lead{border-left-color:var(--r3-crit)}.r3-st-converted .r3-lead{border-left-color:var(--r3-gold)}
.r3-pips{display:flex;gap:4px}.r3-pip{width:22px;height:6px;border-radius:3px;background:var(--r3-line)}.r3-pip.on{background:var(--r3-gold)}.r3-pip.cur{background:var(--r3-brand)}
.r3-stagetxt{font-size:11px;color:var(--r3-muted);margin-top:4px}.r3-money{font-weight:750}.r3-money .of{color:var(--r3-muted);font-weight:600;font-size:11.5px}
.r3-chip-s{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:700;padding:3px 9px;border-radius:999px;white-space:nowrap}.r3-chip-s .dot{width:6px;height:6px;border-radius:50%;background:currentColor}
.s-converting{color:var(--r3-good);background:var(--r3-goodsoft)}.s-pending{color:var(--r3-ink2);background:var(--r3-line2)}.s-expiring{color:var(--r3-warn);background:var(--r3-warnsoft)}.s-lastcall{color:var(--r3-crit);background:var(--r3-critsoft)}.s-lapsed{color:var(--r3-crit);background:var(--r3-critsoft)}.s-converted{color:var(--r3-gold);background:var(--r3-goldsoft)}
.r3-src{display:inline-block;font-size:11px;font-weight:700;padding:3px 9px;border-radius:999px;white-space:nowrap;color:var(--r3-ink2);background:var(--r3-line2)}
.r3-src.ios{color:#0b6bb3;background:#e6f1fb}.r3-src.android{color:#1f8a4c;background:#e5f5ec}.r3-src.web{color:#5a52c9;background:#ecebfa}.r3-src.wa{color:#1f8a4c;background:#e5f5ec}.r3-src.store{color:#a9781f;background:var(--r3-goldsoft)}.r3-src.del{color:var(--r3-warn);background:var(--r3-warnsoft)}.r3-src.other{color:var(--r3-faint);background:var(--r3-line2)}
.r3-conv{display:inline-block;font-size:11px;font-weight:700;padding:3px 9px;border-radius:999px;white-space:nowrap}
.r3-conv.c-reward{color:#a9781f;background:#f6edda}.r3-conv.c-natural{color:#1f8a4c;background:#e5f5ec}.r3-conv.c-other{color:#0b6bb3;background:#e6f1fb}.r3-conv-none{color:var(--r3-faint);font-weight:700}
.r3-drange{display:flex;align-items:center;flex-wrap:wrap;gap:8px;margin:0 0 16px}.r3-drlab{font-size:11px;font-weight:700;letter-spacing:.04em;color:var(--r3-muted);text-transform:uppercase;margin-right:2px}
.r3-drchip{border:1px solid var(--r3-line);background:var(--r3-card);color:var(--r3-ink2);border-radius:8px;padding:6px 12px;font-size:12.5px;font-weight:600;cursor:pointer}.r3-drchip.on{background:var(--r3-ink);color:#fff;border-color:var(--r3-ink)}
.r3-drcustom{display:inline-flex;align-items:center;gap:6px;background:var(--r3-card);border:1px solid var(--r3-line);border-radius:8px;padding:4px 8px}.r3-drcustom input[type=date]{border:0;background:transparent;font:inherit;font-size:12.5px;color:var(--r3-ink);outline:none}.r3-drto{color:var(--r3-faint);font-size:12px}.r3-drapply{border:0;background:var(--r3-brand);color:#fff;border-radius:6px;padding:5px 12px;font-size:12.5px;font-weight:700;cursor:pointer}
.r3-cmix{max-width:640px;margin:0 0 16px}.r3-cmix-lab{font-size:11px;font-weight:700;letter-spacing:.04em;color:var(--r3-muted);text-transform:uppercase;margin:0 0 7px}
.r3-cmix-bar{display:flex;height:24px;border-radius:7px;overflow:hidden;border:1px solid var(--r3-line);margin:0 0 8px}.r3-cmix-bar>span{display:block;height:100%}
.r3-cmix-legs{display:flex;flex-wrap:wrap;gap:14px;font-size:12.5px;color:var(--r3-ink2)}.r3-cmix-leg .dot{display:inline-block;width:9px;height:9px;border-radius:2px;margin-right:5px}
.r3-rdots{display:flex;gap:5px}.r3-rdot{width:20px;height:20px;border-radius:6px;display:grid;place-items:center;font-size:10px;font-weight:800;border:1px solid var(--r3-line);color:var(--r3-faint);background:#faf8fc}
.r3-rdot.sent{background:var(--r3-brandsoft);border-color:#e6bcd5;color:var(--r3-brand)}.r3-rdot.due{background:var(--r3-warnsoft);border-color:#e6c98a;color:var(--r3-warn)}
.r3-clock{font-weight:750;font-size:12.5px}.r3-clock.warn{color:var(--r3-warn)}.r3-clock.crit{color:var(--r3-crit)}.r3-clock.ok{color:var(--r3-muted)}.r3-clock.done{color:var(--r3-gold)}.r3-clock small{display:block;font-weight:600;color:var(--r3-muted);font-size:10.5px}
.r3-nextact{font-size:12px;color:var(--r3-ink2)}.r3-nextact b{color:var(--r3-brand)}
.r3-empty{padding:44px;text-align:center;color:var(--r3-muted)}
.r3-foot{margin-top:16px;font-size:11.5px;color:var(--r3-muted)}.r3-foot b{color:var(--r3-ink2)}
@media(max-width:1200px){.r3-kpis{grid-template-columns:repeat(3,1fr)}.r3-grid2{grid-template-columns:1fr}}
`;
